import { Button, FormLayout, TextInput } from 'components/common'
import AggregatorDropdown from 'components/common/aggregatorDropdown'
import SwitchField from 'components/bundleProduct/switchField'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ConfigurationWithId } from 'types/configuration'
import { getInputErrorMessage } from 'utils/helpers/getNestedErrorMessage'
import { handleError } from 'utils/helpers/handleError'
import StoresDropdown from './storesDropdown'
import { magentoConfigFields } from './utils'
import {
  magentoConfigSchema,
  MagentoConfigFormFields,
} from 'utils/schema/magentoConfigSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  triggerMagentoInventorySync,
  updateMagentoConfig,
} from 'utils/api/configApi'

const {
  STORE,
  AGGREGATOR,
  MAGENTO_BASE_URL,
  MAGENTO_TOKEN,
  INVENTORY_LIMIT,
  PRICING_LIMIT,
  CATALOGUE_LIMIT,
  PURCHASE_TIMER_LIMIT,
  IS_INVENTORY_SYNC_ENABLED,
  INVENTORY_SYNC_CHUNK_SIZE,
  SYNC_BUNDLE_PRODUCT,
} = magentoConfigFields

type Props = {
  configuration?: ConfigurationWithId
}

const MagentoConfigurationForm = ({ configuration }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<MagentoConfigFormFields>({
    resolver: yupResolver(magentoConfigSchema),
    defaultValues: {
      ...(configuration?.magentoApi || {}),
    },
  })

  const [loading, setLoading] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)

  const isInventorySyncEnabled = watch(IS_INVENTORY_SYNC_ENABLED.key)

  const handleSave = async (data: MagentoConfigFormFields) => {
    const configToast = toast.loading('Updating Magento config...')
    setLoading(true)
    try {
      await updateMagentoConfig(data)
      toast.success('Magento config updated successfully.', {
        id: configToast,
      })
    } catch (error) {
      handleError(error, configToast, {
        default: 'Failed to update Magento config.',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleManualSync = async () => {
    const syncToast = toast.loading('Triggering Magento inventory sync...')
    setSyncLoading(true)
    try {
      const { data } = await triggerMagentoInventorySync()
      toast.success(
        data?.message || 'Magento inventory sync triggered successfully.',
        {
          id: syncToast,
        },
      )
    } catch (error) {
      handleError(error, syncToast, {
        default: 'Failed to trigger Magento inventory sync.',
      })
    } finally {
      setSyncLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      {/* ================= MAGENTO API CONFIGURATION ================= */}
      <h3 className="title mt-4">Magento API Configuration</h3>

      <FormLayout>
        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            register={register}
            name={MAGENTO_BASE_URL.key}
            label={MAGENTO_BASE_URL.label}
            error={getInputErrorMessage(errors, MAGENTO_BASE_URL)}
            placeholder="https://magento.example.com"
          />

          <TextInput
            required
            type="password"
            register={register}
            name={MAGENTO_TOKEN.key}
            label={MAGENTO_TOKEN.label}
            error={getInputErrorMessage(errors, MAGENTO_TOKEN)}
          />
        </div>
      </FormLayout>

      {/* ================= PULL API PAGE LIMITS ================= */}
      <h3 className="title mt-4">Pull API Page Limits</h3>

      <FormLayout>
        <div className="grid gap-4 mid-md:grid-cols-3">
          <TextInput
            required
            type="number"
            min={1}
            step={1}
            register={register}
            name={INVENTORY_LIMIT.key}
            label={INVENTORY_LIMIT.label}
            error={getInputErrorMessage(errors, INVENTORY_LIMIT)}
          />

          <TextInput
            required
            type="number"
            min={1}
            step={1}
            register={register}
            name={CATALOGUE_LIMIT.key}
            label={CATALOGUE_LIMIT.label}
            error={getInputErrorMessage(errors, CATALOGUE_LIMIT)}
          />

          <TextInput
            type="number"
            min={1}
            step={1}
            register={register}
            name={PURCHASE_TIMER_LIMIT.key}
            label={PURCHASE_TIMER_LIMIT.label}
            error={getInputErrorMessage(errors, PURCHASE_TIMER_LIMIT)}
          />
        </div>
      </FormLayout>

      {/* ================= PRICING PULL API (AGGREGATOR -> MAGENTO) ================= */}
      <h3 className="title mt-4">Pricing Pull API (Aggregator → Magento)</h3>

      <FormLayout>
        <div className="grid gap-4 mid-md:grid-cols-3">
          <StoresDropdown
            withNoneOption
            setValue={setValue}
            defaultValue={configuration?.magentoApi?.store}
            name={STORE.key}
            label={STORE.label}
            error={getInputErrorMessage(errors, STORE)}
          />

          <AggregatorDropdown
            withNoneOption
            setValue={setValue}
            defaultValue={configuration?.magentoApi?.aggregator}
            name={AGGREGATOR.key}
            label={AGGREGATOR.label}
            error={getInputErrorMessage(errors, AGGREGATOR)}
          />

          <TextInput
            required
            type="number"
            min={1}
            step={1}
            register={register}
            name={PRICING_LIMIT.key}
            label={PRICING_LIMIT.label}
            error={getInputErrorMessage(errors, PRICING_LIMIT)}
          />
        </div>
      </FormLayout>

      {/* ================= BUNDLE PRODUCT SYNC ================= */}
      <h3 className="title mt-4">Bundle Product Sync</h3>

      <FormLayout>
        <SwitchField
          control={control}
          name={SYNC_BUNDLE_PRODUCT.key}
          label={SYNC_BUNDLE_PRODUCT.label}
          description="Enable or disable syncing bundle products to Magento"
          showStatusPill
        />
      </FormLayout>

      {/* ================= INVENTORY SYNC CONFIGURATION ================= */}
      <h3 className="title mt-4">Inventory Sync Configuration</h3>

      <FormLayout>
        <SwitchField
          control={control}
          name={IS_INVENTORY_SYNC_ENABLED.key}
          label={IS_INVENTORY_SYNC_ENABLED.label}
          description="Enable or disable the Magento inventory push sync"
          showStatusPill
        />

        <div className="mb-5 border-t" />

        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            type="number"
            min={1}
            step={1}
            register={register}
            name={INVENTORY_SYNC_CHUNK_SIZE.key}
            label={INVENTORY_SYNC_CHUNK_SIZE.label}
            error={getInputErrorMessage(errors, INVENTORY_SYNC_CHUNK_SIZE)}
            placeholder="5000"
          />
        </div>

        <div className="mt-5 flex items-center gap-3 border-t pt-5">
          <Button
            type="button"
            variant="outline"
            loading={syncLoading}
            disabled={syncLoading || loading || !isInventorySyncEnabled}
            buttonText="Sync Inventory"
            className="w-48"
            onClick={handleManualSync}
          />
        </div>
      </FormLayout>

      <div className="mt-5 flex items-center gap-3">
        <Button loading={loading} buttonText="Save" className="w-32" />
      </div>
    </form>
  )
}

export default MagentoConfigurationForm
